import { useSelector } from 'react-redux';
import { getImageSrc } from '../../../../utils/fn';
import { eventTimeFormat } from '../../../../utils/time';
import { LogoImage } from '../EventPosterSectionMenu/styles';
import {
  Wrapper,
  BurgerButton,
  FullScreenOverlay,
  MenuContent,
  MobileMenuList,
  MobileMenuItemGroup,
  MobileMenuItem,
  SubMenuList,
  SubMenuItem,
  CoLocatedEventsItem,
  CoLocatedEventsList,
  CoLocatedEventLogoWrapper,
  CoLocatedEventLogo,
  EventInfoSection,
  EventInfoItem,
  EventInfoText,
} from './styles';
import { getEventDetails } from '../../../../store/slices/eventSlice';
import { useMemo, useState, useEffect, useCallback } from 'react';
import weLogo from '@assets/images/weCONECT_col_RGB.svg';
import { usePublicCoLocatedEventsByEventId } from '../../../../utils/apiHooks';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Button from '@components/Button';
import { MenuItem as MenuItemType } from '@hubs101/booking-api-skd-client/lib/types';
import Icon from '@components/Icon';
import { getLanguage } from '@store/slices/configSlice';
import { getMailToLink, mappingLinks } from '../EventPosterSectionMenu';

export const ScrollMenuBarMobile = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [expandedItemIndex, setExpandedItemIndex] = useState<number | null>(
    null
  );
  const [isCoLocatedExpanded, setIsCoLocatedExpanded] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const language = useSelector(getLanguage);

  const { data: coLocatedEvents } = usePublicCoLocatedEventsByEventId({
    eventId: sluggedEvent?.id,
    enabled: sluggedEvent?.id?.length > 0 && sluggedEvent?.colocated,
  });

  // Process menu items from the event data
  const sortedMenuItems = useMemo(
    () =>
      sluggedEvent?.website_menu
        ?.find(e => e.type === 'mainmenu')
        ?.items?.toSorted(
          (a: MenuItemType, b: MenuItemType) => a.order - b.order
        ),
    [sluggedEvent?.website_menu]
  );

  const link = useCallback(
    (item: MenuItemType) => {
      switch (item.type) {
        case 'internal':
          return `/${String(item?.url ?? '')}`;
        case 'external':
          return item?.url ?? '';
        case 'mailto':
          return getMailToLink({
            sluggedEvent: sluggedEvent,
            coLocatedEvents: coLocatedEvents,
            email: item?.to ?? '',
            subject: item?.subject ?? '',
            body: item?.body ?? '',
          });
        default:
          return t(
            mappingLinks[item.type as keyof typeof mappingLinks] ??
              `/${String(item.url)}`
          );
      }
    },
    [t]
  );

  const mainMenuItems = useMemo(
    () =>
      sortedMenuItems
        ?.filter(e => !e.level)
        ?.map(item => {
          return {
            id: item.label,
            title: item.label,
            subtitle: item.subtitle,
            link: link(item),
            order: item.order,
          };
        }),
    [link, sortedMenuItems]
  );

  const secondaryMenuItems = useMemo(
    () =>
      sortedMenuItems
        ?.filter(e => e.level)
        ?.map(item => {
          return {
            id: item.label,
            title: item.label,
            subtitle: item.subtitle,
            link: link(item),
            order: item.order,
            index: mainMenuItems?.findLastIndex(
              element => element.order < item.order
            ),
          };
        }) ?? [],
    [link, mainMenuItems, sortedMenuItems]
  );

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const maxScroll = 100;
      const progress = Math.min(scrollY / maxScroll, 1); // Clamp between 0 and 1

      setIsScrolled(scrollY > 0);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <Wrapper $isScrolled={isScrolled} $scrollProgress={scrollProgress}>
        <LogoImage
          $maxWidth={'10rem'}
          src={
            sluggedEvent?.design?.eventLogoPosSvg?.length > 0 &&
            sluggedEvent?.design?.eventLogoNegSvg?.length > 0
              ? getImageSrc(
                  isMenuOpen || (scrollProgress && scrollProgress > 0)
                    ? sluggedEvent?.design?.eventLogoPosSvg
                    : sluggedEvent?.design?.eventLogoNegSvg
                )
              : weLogo
          }
          alt={
            sluggedEvent?.name?.length ? sluggedEvent?.name : 'WeConect Logo'
          }
          $height={`${5}rem`}
        />

        <BurgerButton
          onClick={toggleMenu}
          $isOpen={isMenuOpen}
          $scrollProgress={scrollProgress}
        >
          {isMenuOpen ? (
            <Icon name="close" color={theme.color.grey2} size={30} />
          ) : (
            <Icon
              name="menu"
              color={
                scrollProgress && scrollProgress > 0
                  ? theme.color.grey2
                  : theme.color.primaryText
              }
              size={30}
            />
          )}
        </BurgerButton>
      </Wrapper>

      <FullScreenOverlay $isOpen={isMenuOpen} onClick={closeMenu}>
        <MenuContent
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <MobileMenuList>
            {mainMenuItems?.map((mainItem, index) => {
              const subItems = secondaryMenuItems?.filter(
                item => item.index === index
              );
              const hasSubItems = subItems && subItems.length > 0;
              const isExpanded = expandedItemIndex === index;

              return (
                <MobileMenuItemGroup key={mainItem.id}>
                  <MobileMenuItem
                    onClick={() => {
                      if (hasSubItems) {
                        // Toggle expansion if has sub items
                        setExpandedItemIndex(isExpanded ? null : index);
                      } else {
                        // Navigate if no sub items
                        navigate(
                          `/events/${sluggedEvent?.slug}${mainItem.link}`
                        );
                        closeMenu();
                      }
                    }}
                    $themeColor={sluggedEvent?.design?.themeColor}
                    $hasSubItems={hasSubItems}
                  >
                    {mainItem.title}
                  </MobileMenuItem>

                  {hasSubItems && isExpanded && (
                    <SubMenuList>
                      {subItems.map(subItem => (
                        <SubMenuItem
                          key={subItem.id}
                          onClick={() => {
                            navigate(
                              `/events/${sluggedEvent?.slug}${subItem.link}`
                            );
                            closeMenu();
                          }}
                          $themeColor={sluggedEvent?.design?.themeColor}
                        >
                          {subItem.title}
                        </SubMenuItem>
                      ))}
                    </SubMenuList>
                  )}
                </MobileMenuItemGroup>
              );
            })}
          </MobileMenuList>

          {sluggedEvent?.colocated &&
            coLocatedEvents &&
            coLocatedEvents.length > 0 && (
              <MobileMenuItemGroup>
                <CoLocatedEventsItem
                  onClick={() => {
                    setIsCoLocatedExpanded(!isCoLocatedExpanded);
                  }}
                >
                  Co-Located Events
                </CoLocatedEventsItem>

                {isCoLocatedExpanded && (
                  <CoLocatedEventsList>
                    {coLocatedEvents
                      .filter(event => event.slug !== sluggedEvent?.slug)
                      .map(event => (
                        <CoLocatedEventLogoWrapper
                          key={event.id}
                          onClick={() => {
                            navigate(`/events/${event.slug}`);
                            closeMenu();
                          }}
                        >
                          <CoLocatedEventLogo
                            src={getImageSrc(
                              event.design?.eventLogoPosSvg ?? ''
                            )}
                            alt={event.name}
                          />
                        </CoLocatedEventLogoWrapper>
                      ))}
                  </CoLocatedEventsList>
                )}
              </MobileMenuItemGroup>
            )}

          {sluggedEvent?.start_date && (
            <EventInfoSection>
              <EventInfoItem>
                <Icon
                  name="add_calender"
                  color={
                    sluggedEvent?.design?.themeColor || theme.color.primaryText
                  }
                  size={20}
                />
                <EventInfoText>
                  {eventTimeFormat(
                    sluggedEvent?.start_date ?? '',
                    sluggedEvent?.end_date ?? '',
                    language,
                    sluggedEvent?.timezone
                  )}
                </EventInfoText>
              </EventInfoItem>

              {sluggedEvent?.location?.name && (
                <EventInfoItem>
                  <Icon
                    name="location"
                    color={
                      sluggedEvent?.design?.themeColor ||
                      theme.color.primaryText
                    }
                    size={20}
                  />
                  <EventInfoText>{sluggedEvent?.location?.name}</EventInfoText>
                </EventInfoItem>
              )}
            </EventInfoSection>
          )}

          <div
            style={{
              marginTop: '2rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}
          >
            <Button
              title={t('book-now')}
              backgroundColor={sluggedEvent?.design?.themeColor}
              hoverBackgroundColor={theme.color.primaryText}
              textColor={theme.color.white}
              hoverTextColor={theme.color.white}
              borderRadius={theme.borderRadius.radius10}
              fontSize={theme.fontSize.size20}
              lineHeight={theme.lineHeight.height150}
              fontWeight="normal"
              padding="1.1rem 10rem"
              border="1px solid transparent"
              width="100%"
              onClick={() => {
                navigate(`/events/${sluggedEvent?.slug}${t('book-now-url')}`);
                closeMenu();
              }}
            />

            <Button
              title={t('exhibit-now')}
              backgroundColor={theme.color.transparent}
              hoverBackgroundColor={sluggedEvent?.design?.themeColor}
              textColor={sluggedEvent?.design?.themeColor}
              hoverTextColor={theme.color.white}
              border={`1px solid ${sluggedEvent?.design?.themeColor}`}
              borderRadius={theme.borderRadius.radius10}
              fontSize={theme.fontSize.size20}
              lineHeight={theme.lineHeight.height150}
              fontWeight="normal"
              padding="1.1rem 10rem"
              width="100%"
              onClick={() => {
                navigate(`/events/${sluggedEvent?.slug}/bp-packages`);
                closeMenu();
              }}
            />
          </div>
        </MenuContent>
      </FullScreenOverlay>
    </>
  );
};
